import type { Hex } from 'viem';

import config from './config.json';

// oxlint-disable-next-line no-restricted-properties
const { hostname, search } = window.location;
const urlParams = new URLSearchParams(search);

// Closed allowlist — untrusted `allowedOrigin` query would bypass postMessage trust (HackerOne #3739305).
const allowedParentOriginPatterns = (walletHostname: string): RegExp[] => {
  if (/(^|\.)wallet\.sorare\.dev$/.test(walletHostname)) {
    return [/^https:\/\/(preview\.)?sorare\.dev$/];
  }
  if (/(^|\.)wallet\.sorare\.tech$/.test(walletHostname)) {
    return [/^https:\/\/(preview\.)?sorare\.tech$/];
  }
  if (walletHostname === 'wallet.sorare.com') {
    return [/^https:\/\/sorare\.com$/];
  }
  if (
    /^([a-z0-9-]+\.)*localhost$|^[a-z0-9-]+\.local$|^192\.168\.\d+\.\d+$|^10\.0\.2\.2$/.test(
      walletHostname
    )
  ) {
    return [
      /^https?:\/\/(([a-z0-9-]+\.)*localhost|[a-z0-9-]+\.local|192\.168\.\d+\.\d+|10\.0\.2\.2)(:\d+)?$/,
    ];
  }
  return [];
};

export const validateAllowedOrigin = (
  walletHostname: string,
  rawAllowedOrigin: string | null
): string | null => {
  if (!rawAllowedOrigin) return null;
  return allowedParentOriginPatterns(walletHostname).some(re =>
    re.test(rawAllowedOrigin)
  )
    ? rawAllowedOrigin
    : null;
};

export const ALLOWED_ORIGIN = validateAllowedOrigin(
  hostname,
  urlParams.get('allowedOrigin')
);

const getEnvConfig = () => {
  if (hostname.match(/sorare.(dev|tech)/)) {
    return config.staging;
  }
  if (hostname.match(/localhost|\.local$/)) {
    const { port } = document.referrer
      ? new URL(document.referrer)
      : // oxlint-disable-next-line no-restricted-properties
        window.location;
    switch (port) {
      case '3003':
        return config.production;
      case '4003':
      case '5003':
        return config.staging;
      default:
        return config.local;
    }
  }
  if (hostname.match(/sorare.netlify.app/)) {
    return config.preview;
  }

  return config.production;
};

const envConfig = getEnvConfig();

export const RELAY_ADDRESS = envConfig.relayAddress;
export const ERC721_ADDRESS = envConfig.erc721Address;
export const MIGRATOR_ADDRESS = envConfig.migratorAddress! as Hex;
export const BANK_DEPOSIT_ADDRESS = envConfig.bankDepositAddress!;
export const BASE_L1_MESSENGER_ADDRESS =
  envConfig.baseL1MessengerAddress as Hex;
export const STARKEX_ADDRESS = envConfig.starkExAddress as Hex;
export const BASE_CROSS_DOMAIN_DEPOSIT_ADDRESS =
  envConfig.baseCrossDomainDepositAddress as Hex;
export const ETHEREUM_CHAIN_ID = envConfig.ethereumChainId;
export const EVM_ENDPOINT = envConfig.evmEndpoint;
export const EVERVAULT_CONFIG = envConfig.evervault;
export const ENCRYPTION_KEYS = {
  sorare: envConfig.sorareEncryptionKey,
  vaultinum: envConfig.vaultinumEncryptionKey,
};
export const SOLANA_RPC_ENDPOINT = envConfig.solanaRpcEndpoint;
